import type { JSX } from 'react';
import { outcomeCodeSchema } from '@majuscule/contracts';
import type { z } from 'zod';

import { Badge, type BadgeVariant } from '@/components/ui/badge';

/**
 * Teintes historiques des statuts. Elles se traduisent en variantes de `Badge`
 * (forme + teinte + mot) : green = success, amber = warning, red = danger,
 * blue / violet = info, gray = neutral.
 */
export type Tone = 'green' | 'red' | 'amber' | 'blue' | 'violet' | 'gray';

/** Variante de `Badge` d'une teinte historique. */
export function toneToBadgeVariant(tone: Tone): BadgeVariant {
  switch (tone) {
    case 'green':
      return 'success';
    case 'amber':
      return 'warning';
    case 'red':
      return 'danger';
    case 'blue':
    case 'violet':
      return 'info';
    case 'gray':
      return 'neutral';
  }
}

/** Badge d'un statut déjà traduit (`requestStatusLabel(...)`, `periodStatusLabel(...)`...). */
export function StatusBadge({ status }: { status: ToneLabel }): JSX.Element {
  return <Badge variant={toneToBadgeVariant(status.tone)}>{status.label}</Badge>;
}

export interface ToneLabel {
  tone: Tone;
  label: string;
}

export function requestStatusLabel(status: string): ToneLabel {
  // Covers every value of the contract's requestStatusSchema (PRD statuses).
  switch (status) {
    case 'succeeded':
      return { tone: 'green', label: 'Réussie' };
    case 'failed':
      return { tone: 'red', label: 'Échouée' };
    case 'running':
      return { tone: 'amber', label: 'En cours' };
    case 'queued':
      return { tone: 'blue', label: 'En file' };
    case 'batched':
      return { tone: 'blue', label: 'En lot' };
    case 'paused':
      return { tone: 'amber', label: 'En pause' };
    case 'rejected':
      return { tone: 'red', label: 'Rejetée' };
    case 'cancelled':
      return { tone: 'gray', label: 'Annulée' };
    case 'expired':
      return { tone: 'gray', label: 'Expirée' };
    default:
      return { tone: 'gray', label: status };
  }
}

type OutcomeCode = z.infer<typeof outcomeCodeSchema>;

/** French label of each request outcome (PRD 9): the contract enum forces a label per code. */
const OUTCOME_LABELS: Record<OutcomeCode, string> = {
  succeeded: 'Réussie',
  rejected_invalid_input: 'Entrée invalide',
  rejected_use_case_unavailable: 'Cas d’usage indisponible',
  rejected_user_unknown: 'Utilisateur inconnu',
  rejected_user_inactive: 'Utilisateur désactivé',
  rejected_cap_reached: 'Plafond atteint',
  provider_unavailable: 'Fournisseur indisponible',
  provider_rejected: 'Refusée par le fournisseur',
  provider_unconfirmed: 'Envoi non confirmé',
  processing_error: 'Erreur de traitement',
  content_refused: 'Contenu refusé',
  output_truncated: 'Sortie tronquée',
  invalid_output: 'Sortie invalide',
  cancelled: 'Annulée',
  expired: 'Expirée',
  internal_error: 'Erreur interne',
};

/** French label of an outcome code; a code this dashboard does not know is shown as is. */
export function outcomeLabel(code: string): string {
  const parsed = outcomeCodeSchema.safeParse(code);
  return parsed.success ? OUTCOME_LABELS[parsed.data] : code;
}

export function periodStatusLabel(status: string): ToneLabel {
  switch (status) {
    case 'open':
      return { tone: 'blue', label: 'Ouverte' };
    case 'closing':
      return { tone: 'amber', label: 'Clôture en cours' };
    case 'invoiced':
      return { tone: 'blue', label: 'Facturée' };
    case 'paid':
      return { tone: 'green', label: 'Payée' };
    case 'payment_failed':
      return { tone: 'red', label: 'Paiement échoué' };
    case 'empty':
      return { tone: 'gray', label: 'Vide' };
    case 'deferred':
      return { tone: 'amber', label: 'Reportée' };
    case 'carried':
      return { tone: 'gray', label: 'Facturée plus tard' };
    default:
      return { tone: 'gray', label: status };
  }
}

export function clientStatusLabel(status: string): ToneLabel {
  switch (status) {
    case 'active':
      return { tone: 'green', label: 'Actif' };
    case 'paused':
      return { tone: 'amber', label: 'Suspendu' };
    case 'disabled':
      return { tone: 'gray', label: 'Désactivé' };
    default:
      return { tone: 'gray', label: status };
  }
}
